import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { Mic } from 'lucide-react'

// localStorage key for remembering where the user dragged the launcher.
const LAUNCHER_OFFSET_KEY = 'ilandsLauncherOffset'
// Minimum pointer travel (px) before a press is treated as a drag rather than a click.
const DRAG_THRESHOLD = 4
// Keep the launcher at least this many px inside the viewport edges.
const VIEWPORT_MARGIN = 8

interface VoiceBotButtonProps {
  onClick: () => void
  isActive: boolean
  /**
   * Either a corner preset, or an explicit offset object. When an object is
   * passed, only the keys that are set are applied so the launcher anchors to
   * those edges.
   */
  position:
    | 'bottom-right'
    | 'bottom-left'
    | 'top-right'
    | 'top-left'
    | 'center'
    | 'bottom-center'
    | { top?: number | string; bottom?: number | string; left?: number | string; right?: number | string }
  primaryColor: string
  iconUrl?: string
  /**
   * Optional custom text for the speech bubble shown above the launcher.
   *  - undefined / null → built-in default ("לחצו עלי ונדבר...")
   *  - empty string → bubble is hidden
   *  - non-empty string → displayed as-is
   */
  bubbleText?: string | null
  /**
   * Optional custom call-to-action text shown directly UNDER the launcher icon.
   *  - undefined / null → built-in default ("לחצו עליי ונשוחח")
   *  - empty string → CTA is hidden
   *  - non-empty string → displayed as-is
   */
  ctaText?: string | null
  zIndex: number
  /**
   * When false the launcher is fixed in place (no drag-to-reposition). The
   * remembered drag offset and pointer handlers are skipped entirely. Defaults
   * to true so existing customers keep the draggable launcher unchanged.
   */
  draggable?: boolean
  /**
   * When true the launcher renders in the normal document flow (position:
   * relative, centered) instead of as a fixed floating button. Used only by the
   * Callega demo landing page on mobile; drag is force-disabled and the speech
   * bubble is forced to a single line. Defaults to false (unchanged behavior).
   */
  inline?: boolean
  /**
   * Callega demo landing-page effects (both breakpoints). When true the rising
   * 0/1 particles emanate from the avatar's circle border OUTWARD (instead of
   * drifting up across the avatar face). Combined with `inline` (mobile) it also
   * removes the hard ripple rings and softens the glow into a gentle halo.
   * Other customers never pass this, so their launcher is byte-identical.
   */
  landingFx?: boolean
}

/**
 * Floating button to open the voice bot panel with eye-catching animations
 */
export const VoiceBotButton: React.FC<VoiceBotButtonProps> = ({
  onClick,
  isActive,
  position,
  primaryColor,
  iconUrl,
  bubbleText,
  ctaText,
  zIndex,
  draggable = true,
  inline = false,
  landingFx = false,
}) => {
  // Master switch for the drag-to-reposition behavior. When disabled the
  // launcher renders at its anchored position with no offset and no handlers.
  // Inline mode is always non-draggable (it lives in the document flow).
  const dragEnabled = draggable !== false && !inline
  const positionStyles: React.CSSProperties = (() => {
    if (typeof position === 'string') {
      switch (position) {
        case 'bottom-left':
          return { left: 24, bottom: 24 }
        case 'top-right':
          return { right: 24, top: 24 }
        case 'top-left':
          return { left: 24, top: 24 }
        case 'center':
          return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
        case 'bottom-center':
          return { bottom: 24, left: '50%', transform: 'translateX(-50%)' }
        case 'bottom-right':
        default:
          return { right: 24, bottom: 24 }
      }
    }
    const out: React.CSSProperties = {}
    if (position.top !== undefined) out.top = position.top
    if (position.bottom !== undefined) out.bottom = position.bottom
    if (position.left !== undefined) out.left = position.left
    if (position.right !== undefined) out.right = position.right
    // Sensible default if caller passed an empty object
    if (out.top === undefined && out.bottom === undefined) out.bottom = 24
    if (out.left === undefined && out.right === undefined) out.right = 24
    // Dashboard percentage coordinates describe the launcher's CENTER (the
    // picker preview also uses translate(-50%, -50%)). Keep legacy pixel and
    // direct embed coordinates edge-anchored so existing integrations do not
    // move when this percentage behavior is introduced.
    const isPercent = (value: unknown) => typeof value === 'string' && value.trim().endsWith('%')
    const translateX = isPercent(out.left) ? '-50%' : isPercent(out.right) ? '50%' : '0'
    const translateY = isPercent(out.top) ? '-50%' : isPercent(out.bottom) ? '50%' : '0'
    out.transform = `translate(${translateX}, ${translateY})`
    return out
  })()

  // Generate random binary digits for the smoke effect
  const binaryParticles = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      id: i,
      digit: Math.random() > 0.5 ? '1' : '0',
      startX: (Math.random() - 0.5) * 40, // Random horizontal offset
      endX: (Math.random() - 0.5) * 80,
      delay: Math.random() * 2,
      duration: 3 + Math.random() * 2,
      // Emission angle for landing-page mode: strictly the upper hemisphere so
      // the particles fan up-and-out from the avatar border and never travel
      // downward across the face. (sin is always <= 0 here → upward.)
      angle: -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.95,
    }))
  }, [])

  // ----- Drag-to-reposition -------------------------------------------------
  // The whole launcher (speech bubble + icon + CTA) can be dragged anywhere on
  // screen. We translate the fixed-positioned wrapper by a px offset on top of
  // its anchored corner. A small travel threshold keeps a normal tap working as
  // a click that opens the panel; only real drags suppress the click.
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>(() => {
    if (!dragEnabled || typeof window === 'undefined') return { x: 0, y: 0 }
    try {
      const raw = window.localStorage.getItem(LAUNCHER_OFFSET_KEY)
      if (raw) {
        const parsed = JSON.parse(raw)
        if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
          return { x: parsed.x, y: parsed.y }
        }
      }
    } catch {
      /* ignore malformed/blocked storage */
    }
    return { x: 0, y: 0 }
  })
  const [isDragging, setIsDragging] = useState(false)
  const dragOffsetRef = useRef(dragOffset)
  const movedRef = useRef(false)
  const pointerStateRef = useRef<{
    pointerId: number | null
    startX: number
    startY: number
    baseX: number
    baseY: number
  }>({ pointerId: null, startX: 0, startY: 0, baseX: 0, baseY: 0 })

  const applyOffset = useCallback((next: { x: number; y: number }) => {
    dragOffsetRef.current = next
    setDragOffset(next)
  }, [])

  // Clamp a desired offset so the launcher stays inside the viewport.
  const clampOffset = useCallback((desired: { x: number; y: number }) => {
    const el = wrapperRef.current
    if (!el || typeof window === 'undefined') return desired
    const rect = el.getBoundingClientRect()
    const cur = dragOffsetRef.current
    // Position of the wrapper if the drag offset were zero.
    const baseLeft = rect.left - cur.x
    const baseTop = rect.top - cur.y
    const minX = VIEWPORT_MARGIN - baseLeft
    const maxX = window.innerWidth - VIEWPORT_MARGIN - rect.width - baseLeft
    const minY = VIEWPORT_MARGIN - baseTop
    const maxY = window.innerHeight - VIEWPORT_MARGIN - rect.height - baseTop
    const clampAxis = (v: number, lo: number, hi: number) =>
      hi < lo ? lo : Math.min(Math.max(v, lo), hi)
    return {
      x: clampAxis(desired.x, minX, maxX),
      y: clampAxis(desired.y, minY, maxY),
    }
  }, [])

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return
      pointerStateRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        baseX: dragOffsetRef.current.x,
        baseY: dragOffsetRef.current.y,
      }
      movedRef.current = false
    },
    []
  )

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const st = pointerStateRef.current
      const pid = st.pointerId
      if (pid === null) return
      const dx = e.clientX - st.startX
      const dy = e.clientY - st.startY
      if (!movedRef.current && Math.hypot(dx, dy) < DRAG_THRESHOLD) return
      if (!movedRef.current) {
        movedRef.current = true
        // Capture only once an actual drag starts so a plain tap still fires click.
        try {
          wrapperRef.current?.setPointerCapture(pid)
        } catch {
          /* capture is best-effort */
        }
      }
      if (!isDragging) setIsDragging(true)
      applyOffset(clampOffset({ x: st.baseX + dx, y: st.baseY + dy }))
    },
    [applyOffset, clampOffset, isDragging]
  )

  const handlePointerUp = useCallback(
    () => {
      const st = pointerStateRef.current
      if (st.pointerId === null) return
      try {
        wrapperRef.current?.releasePointerCapture(st.pointerId)
      } catch {
        /* best-effort */
      }
      st.pointerId = null
      if (isDragging) setIsDragging(false)
      if (movedRef.current) {
        try {
          window.localStorage.setItem(
            LAUNCHER_OFFSET_KEY,
            JSON.stringify(dragOffsetRef.current)
          )
        } catch {
          /* ignore blocked storage */
        }
      }
    },
    [isDragging]
  )

  // Re-clamp on resize (and once after mount, in case a restored offset is now
  // off-screen on a smaller viewport).
  useEffect(() => {
    if (!dragEnabled || typeof window === 'undefined') return
    const reclamp = () => {
      const clamped = clampOffset(dragOffsetRef.current)
      if (
        clamped.x !== dragOffsetRef.current.x ||
        clamped.y !== dragOffsetRef.current.y
      ) {
        applyOffset(clamped)
        try {
          window.localStorage.setItem(LAUNCHER_OFFSET_KEY, JSON.stringify(clamped))
        } catch {
          /* ignore */
        }
      }
    }
    window.addEventListener('resize', reclamp)
    reclamp()
    return () => window.removeEventListener('resize', reclamp)
  }, [applyOffset, clampOffset, dragEnabled])

  const composedTransform = `${
    positionStyles.transform ? `${positionStyles.transform} ` : ''
  }translate(${dragOffset.x}px, ${dragOffset.y}px)`

  return (
    <div
      ref={wrapperRef}
      onPointerDown={dragEnabled ? handlePointerDown : undefined}
      onPointerMove={dragEnabled ? handlePointerMove : undefined}
      onPointerUp={dragEnabled ? handlePointerUp : undefined}
      onPointerCancel={dragEnabled ? handlePointerUp : undefined}
      style={{
        // Inline: in-flow, centered, no fixed offsets/transform so it scrolls
        // with the page. Fixed (default): unchanged floating-launcher behavior.
        position: inline ? 'relative' : 'fixed',
        ...(inline ? {} : positionStyles),
        transform: inline ? undefined : composedTransform,
        zIndex,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        ...(inline
          ? { margin: '0 auto', touchAction: 'auto', cursor: 'pointer' }
          : {
              touchAction: dragEnabled ? 'none' : 'auto',
              cursor: dragEnabled ? (isDragging ? 'grabbing' : 'grab') : 'default',
            }),
      }}
    >
      {/* Hebrew Text Above Button (customer-customizable; empty/null falls back to default) */}
      {(() => {
        const trimmed = (bubbleText ?? '').toString().trim()
        const resolvedBubble = trimmed.length > 0 ? trimmed : 'לחצו עלי ונדבר...'
        return (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            style={{
              direction: 'rtl',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              fontSize: 16,
              fontWeight: 600,
              color: '#333',
              textShadow: '0 2px 4px rgba(255,255,255,0.8)',
              background: 'rgba(255,255,255,0.95)',
              padding: '8px 16px',
              borderRadius: 20,
              boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
              backdropFilter: 'blur(10px)',
              // Inline (Callega demo) forces the bubble onto a single line.
              ...(inline ? { whiteSpace: 'nowrap' as const } : {}),
            }}
          >
            {resolvedBubble}
          </motion.div>
        )
      })()}



      {/* Ripple Waves (periodic).
          Hidden for the Callega demo (`landingFx`, both breakpoints) where the
          user asked to drop the aggressive "pulsing circles" in favour of a
          gentle halo. All other customers keep the original rings. */}
      {!isActive && !landingFx && [0, 1, 2].map((index) => (
        <motion.div
          key={index}
          style={{
            position: 'absolute',
            width: 96,
            height: 96,
            borderRadius: '50%',
            border: `2px solid ${primaryColor}`,
            bottom: 0,
            left: '50%',
            marginLeft: -48,
            pointerEvents: 'none',
          }}
          animate={{
            scale: [1, 2.5],
            opacity: [0.6, 0],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            delay: index * 1,
            ease: 'easeOut',
          }}
        />
      ))}

      {/* Gentle breathing halo — replaces the dominant ripple on the Callega
          demo launcher (`landingFx`, both breakpoints) with a soft, slow glow
          behind the avatar. */}
      {!isActive && landingFx && (
        <motion.div
          style={{
            position: 'absolute',
            left: '50%',
            bottom: 48,
            width: 130,
            height: 130,
            marginLeft: -65,
            marginBottom: -65,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${primaryColor}33 0%, ${primaryColor}1a 45%, transparent 70%)`,
            pointerEvents: 'none',
            zIndex: 0,
          }}
          animate={{
            scale: [1, 1.18, 1],
            opacity: [0.35, 0.18, 0.35],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      )}

      {/* Binary "Digital Smoke" — radial variant for ALL customers: 0s and 1s
          start on the avatar's circle border (radius 52 > avatar radius 48) and
          travel OUTWARD along their angle, so they never drift across / hide the
          avatar face. */}
      {!isActive && binaryParticles.map((particle) => {
        const r0 = 52
        const r1 = 96 + (particle.id % 4) * 8
        const cos = Math.cos(particle.angle)
        const sin = Math.sin(particle.angle)
        return (
          <motion.div
            key={particle.id}
            style={{
              position: 'absolute',
              left: '50%',
              bottom: 48,
              marginLeft: -6,
              fontSize: 16,
              fontWeight: 700,
              fontFamily: 'monospace',
              color: primaryColor,
              pointerEvents: 'none',
              textShadow: `0 0 8px ${primaryColor}80`,
              zIndex: 10,
            }}
            initial={{
              opacity: 0,
              x: cos * r0,
              y: sin * r0,
              scale: 0.5,
            }}
            animate={{
              opacity: [0, 0.85, 0],
              x: cos * r1,
              y: sin * r1,
              scale: [0.5, 1.05, 0.5],
            }}
            transition={{
              duration: particle.duration,
              repeat: Infinity,
              delay: particle.delay,
              ease: 'easeOut',
            }}
          >
            {particle.digit}
          </motion.div>
        )
      })}

      {/* Main Button */}
      <motion.button
        data-ilands-launcher-button="true"
        onClick={(e) => {
          // Suppress the click that follows a drag; let real taps open the panel.
          if (movedRef.current) {
            movedRef.current = false
            e.preventDefault()
            e.stopPropagation()
            return
          }
          onClick()
        }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{
          scale: 1,
          opacity: 1,
        }}
        exit={{ scale: 0, opacity: 0 }}
        whileHover={
          iconUrl?.endsWith('.mp4')
            ? {
                width: 240,
                height: 135,
                borderRadius: 20,
                transition: { type: 'spring', stiffness: 260, damping: 20 }
              }
            : {
                scale: 1.15,
                rotate: [0, -5, 5, 0],
                transition: {
                  scale: { type: 'spring', stiffness: 260, damping: 20 },
                  rotate: { duration: 0.4 }
                }
              }
        }
        whileTap={{ scale: 0.9 }}
        transition={{ type: 'spring', stiffness: 260, damping: 20 }}
        style={{
          position: 'relative',
          direction: 'ltr',
          width: 96,
          height: 96,
          borderRadius: '50%',
          backgroundImage: `linear-gradient(135deg, ${primaryColor}, ${adjustBrightness(primaryColor, 0.8)}, ${primaryColor})`,
          backgroundSize: '200% 200%',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: landingFx
            ? `0 4px 16px rgba(0, 0, 0, 0.18), 0 0 18px ${primaryColor}26`
            : `0 4px 20px rgba(0, 0, 0, 0.2), 0 0 40px ${primaryColor}40`,
          overflow: 'hidden',
        }}
        aria-label="Open voice assistant"
      >
        {/* Gradient Animation */}
        <motion.div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${primaryColor}, ${adjustBrightness(primaryColor, 1.2)})`,
            opacity: 0.8,
          }}
          animate={{
            rotate: 360,
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'linear',
          }}
        />

        {/* Icon */}
        <div
          style={{ position: 'relative', zIndex: 1 }}
        >
          {iconUrl ? (
            iconUrl.endsWith('.mp4') ? (
              <motion.video
                src={iconUrl}
                autoPlay
                loop
                muted
                playsInline
                style={{
                  width: 86,
                  height: 86,
                  borderRadius: '50%',
                  objectFit: 'cover',
                }}
                whileHover={{
                  width: 240,
                  height: 135,
                  borderRadius: 20,
                  transition: { type: 'spring', stiffness: 260, damping: 20 }
                }}
              />
            ) : (
              <img
                src={iconUrl}
                alt="Voice Bot"
                style={{
                  width: 86,
                  height: 86,
                  borderRadius: '50%',
                  objectFit: 'cover',
                }}
              />
            )
          ) : (
            <Mic size={34} color="white" strokeWidth={2.5} />
          )}
        </div>
      </motion.button>

      {/* Customer-customizable CTA text shown UNDER the launcher icon. */}
      {(() => {
        const trimmedCta = (ctaText ?? '').toString().trim()
        // Show ONLY when the customer set a non-empty value.
        // null / undefined / empty / whitespace → hide (no hardcoded default).
        const resolvedCta = trimmedCta.length > 0 ? trimmedCta : null
        if (!resolvedCta) return null
        return (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            style={{
              direction: 'rtl',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              fontSize: 13,
              fontWeight: 600,
              color: '#fff',
              textShadow: '0 1px 4px rgba(0,0,0,0.55)',
              textAlign: 'center',
              lineHeight: 1.3,
              pointerEvents: 'none',
              marginTop: -2,
              maxWidth: 200,
              // On the Callega landing page the caption sits over a light hero,
              // where plain white text is invisible. Add a readable translucent
              // pill so it reads on both light and dark themes. Other customers
              // keep the original white-on-shadow caption unchanged.
              ...(landingFx
                ? {
                    background: 'rgba(17, 36, 30, 0.72)',
                    padding: '4px 12px',
                    borderRadius: 999,
                    backdropFilter: 'blur(4px)',
                    WebkitBackdropFilter: 'blur(4px)',
                  }
                : {}),
            }}
          >
            {resolvedCta}
          </motion.div>
        )
      })()}

      {/* Pulse animation when active */}
      {isActive && (
        <motion.div
          style={{
            position: 'absolute',
            width: 80,
            height: 80,
            borderRadius: '50%',
            border: `3px solid ${primaryColor}`,
            bottom: 0,
            left: '50%',
            marginLeft: -40,
            pointerEvents: 'none',
          }}
          animate={{
            scale: [1, 1.5, 1.5],
            opacity: [0.8, 0, 0],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeOut',
          }}
        />
      )}
    </div>
  )
}

function adjustBrightness(color: string, factor: number): string {
  const hex = color.replace('#', '')
  const r = Math.round(parseInt(hex.substring(0, 2), 16) * factor)
  const g = Math.round(parseInt(hex.substring(2, 4), 16) * factor)
  const b = Math.round(parseInt(hex.substring(4, 6), 16) * factor)
  const toHex = (n: number) => n.toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

export default VoiceBotButton
